import React, { useRef, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import ExportCollectionModal from "./ExportCollectionModal";
import ImportCollectionModal from "./ImportCollectionModal";
import UserDashboardModal from "./UserDashboardModal";
import "./AccountSettingsModal.css";


export default function AccountSettingsModal({
  show,
  onClose,
  currentUser,
  newDisplayName,
  setNewDisplayName,
  userEmail,
  newEmail,
  setNewEmail,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  handleUpdateAccount,
  updatingAccount,
  isDarkMode,
  setIsDarkMode,
}) {
  const modalRef = useRef(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [profileStorage, setProfileStorage] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [collectionVisibility, setCollectionVisibility] = useState("public");
  const [savedCollectionVisibility, setSavedCollectionVisibility] = useState("public");
  const [memberSince, setMemberSince] = useState(null);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [privacyError, setPrivacyError] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [accountError, setAccountError] = useState("");
  const profileFileRef = useRef(null);


  useEffect(() => {
    if (show && currentUser) {
      setNewDisplayName(currentUser.display_name || "");
      setNewEmail(userEmail || "");
      setSaveStatus("idle");
      setAccountError("");
    }
  }, [show, currentUser, userEmail, setNewDisplayName, setNewEmail]);

  useEffect(() => {
    if (show && modalRef.current) {
      modalRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    if (!show || !currentUser?.id) return;

    let cancelled = false;

    const loadProfilePicture = async () => {
      setProfileError("");

      const { data, error } = await supabase
        .from("users")
        .select(`
          profile_picture_storage_provider,
          profile_picture_storage_bucket,
          profile_picture_object_key,
          collection_visibility,
          created_at
        `)
        .eq("id", currentUser.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("Failed to load profile picture:", error);
        setProfileError("Unable to load profile picture.");
        return;
      }

      const storage = data?.profile_picture_object_key
        ? {
            provider: data.profile_picture_storage_provider,
            bucket: data.profile_picture_storage_bucket,
            objectKey: data.profile_picture_object_key,
          }
        : null;

      setCollectionVisibility(data?.collection_visibility || "public");
      setSavedCollectionVisibility(data?.collection_visibility || "public");
      setMemberSince(data?.created_at || null);
      setProfileStorage(storage);
      setProfilePictureUrl(
        storage?.provider === "r2" && storage.objectKey
          ? `https://media.hobbli.com/${storage.objectKey}`
          : null
      );
    };

    loadProfilePicture();

    return () => {
      cancelled = true;
    };
  }, [show, currentUser?.id]);

  const makeProfileImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        try {
          const size = Math.min(img.naturalWidth, img.naturalHeight);
          const sx = Math.floor((img.naturalWidth - size) / 2);
          const sy = Math.floor((img.naturalHeight - size) / 2);
          const canvas = document.createElement("canvas");
          canvas.width = 512;
          canvas.height = 512;
          const ctx = canvas.getContext("2d");

          if (!ctx) throw new Error("Canvas is unavailable");

          ctx.drawImage(img, sx, sy, size, size, 0, 0, 512, 512);
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(objectUrl);
              if (!blob) {
                reject(new Error("Unable to prepare profile image"));
                return;
              }
              resolve(blob);
            },
            "image/webp",
            0.88
          );
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Unable to read image"));
      };

      img.src = objectUrl;
    });

  const deleteProfileObject = async (objectKey) => {
    if (!objectKey || !currentUser?.id) return;

    const { data, error } = await supabase.functions.invoke("delete-r2-media", {
      body: {
        resourceType: "profile_picture",
        resourceId: currentUser.id,
        objectKey,
      },
    });

    if (error || data?.success !== true) {
      throw new Error(data?.error || error?.message || "Unable to delete profile image");
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !currentUser?.id) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setProfileError("Choose a JPG, PNG, or WebP image.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setProfileError("Profile pictures must be 10 MB or smaller.");
      return;
    }

    setProfileBusy(true);
    setProfileError("");
    let uploadedKey = null;

    try {
      const blob = await makeProfileImage(file);
      const { data: authData, error: authError } =
        await supabase.functions.invoke("create-r2-upload", {
          body: {
            purpose: "profile_picture",
            contentType: "image/webp",
            fileSize: blob.size,
          },
        });

      if (authError || !authData?.uploadUrl || !authData?.storage?.objectKey) {
        throw new Error(authData?.error || authError?.message || "Unable to authorize upload");
      }

      uploadedKey = authData.storage.objectKey;

      const uploadResponse = await fetch(authData.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/webp" },
        body: blob,
      });

      if (!uploadResponse.ok) throw new Error("Profile image upload failed");

      const oldKey = profileStorage?.objectKey || null;
      const { error: updateError } = await supabase
        .from("users")
        .update({
          profile_picture_storage_provider: authData.storage.provider,
          profile_picture_storage_bucket: authData.storage.bucket,
          profile_picture_object_key: uploadedKey,
        })
        .eq("id", currentUser.id);

      if (updateError) throw updateError;

      const nextStorage = {
        provider: authData.storage.provider,
        bucket: authData.storage.bucket,
        objectKey: uploadedKey,
      };
      setProfileStorage(nextStorage);
      setProfilePictureUrl(`https://media.hobbli.com/${uploadedKey}`);

      if (oldKey && oldKey !== uploadedKey) {
        try {
          await deleteProfileObject(oldKey);
        } catch (cleanupError) {
          console.error("Old profile picture cleanup failed:", cleanupError);
        }
      }
    } catch (error) {
      console.error("Profile picture update failed:", error);

      if (uploadedKey) {
        try {
          await deleteProfileObject(uploadedKey);
        } catch (cleanupError) {
          console.error("Failed profile upload cleanup failed:", cleanupError);
        }
      }

      setProfileError(error?.message || "Unable to update profile picture.");
    } finally {
      setProfileBusy(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!profileStorage?.objectKey || !currentUser?.id || profileBusy) return;

    setProfileBusy(true);
    setProfileError("");
    const oldStorage = profileStorage;

    try {
      const { error: updateError } = await supabase
        .from("users")
        .update({
          profile_picture_storage_provider: null,
          profile_picture_storage_bucket: null,
          profile_picture_object_key: null,
        })
        .eq("id", currentUser.id);

      if (updateError) throw updateError;

      setProfileStorage(null);
      setProfilePictureUrl(null);

      try {
        await deleteProfileObject(oldStorage.objectKey);
      } catch (cleanupError) {
        console.error("Removed profile DB identity but R2 cleanup failed:", cleanupError);
        setProfileError("Photo was removed from your profile, but storage cleanup needs attention.");
      }
    } catch (error) {
      console.error("Profile picture removal failed:", error);
      setProfileError(error?.message || "Unable to remove profile picture.");
    } finally {
      setProfileBusy(false);
    }
  };

  const profileInitials = (currentUser?.display_name || userEmail || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "U";

  const handleSettingsSubmit = async (event) => {
    event.preventDefault();
    if (!currentUser?.id || privacyBusy) return;

    setPrivacyBusy(true);
    setPrivacyError("");
    setAccountError("");
    setSaveStatus("saving");

    try {
      if (collectionVisibility !== savedCollectionVisibility) {
        const { error } = await supabase
          .from("users")
          .update({ collection_visibility: collectionVisibility })
          .eq("id", currentUser.id);

        if (error) throw error;
        setSavedCollectionVisibility(collectionVisibility);
      }

      await handleUpdateAccount(event);
      setSaveStatus("saved");
      setShowPasswordFields(false);
      window.setTimeout(() => setSaveStatus("idle"), 2200);
    } catch (error) {
      console.error("Account settings update failed:", error);
      setAccountError(error?.message || "Unable to save account settings.");
      setSaveStatus("error");
    } finally {
      setPrivacyBusy(false);
    }
  };

  const formattedMemberSince = memberSince
    ? new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(new Date(memberSince))
    : "";

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay settings-modal" onClick={onClose}>
        <div
          className={`settings-modal-content ${isDarkMode ? "dark" : ""}`}
          ref={modalRef}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="settings-close" onClick={onClose} aria-label="Close account settings">×</button>

          <header className="settings-header">
            <span className="settings-eyebrow">Your Hobbli Account</span>
            <h2>Account Settings</h2>
            <p>Manage your profile, privacy, account, and preferences.</p>
          </header>

          <section className="settings-section settings-profile-section" aria-label="Profile">
            <div className="profile-picture-settings">
              <div className="profile-picture-preview">
                {profilePictureUrl ? <img src={profilePictureUrl} alt="Your profile" /> : <span aria-hidden="true">{profileInitials}</span>}
              </div>
              <div className="profile-picture-controls">
                <div className="profile-summary">
                  <strong>{newDisplayName || currentUser?.display_name || "Hobbli Collector"}</strong>
                  {formattedMemberSince && <span>Member since {formattedMemberSince}</span>}
                </div>
                <span className="profile-picture-help">Your photo appears with Hobbli activity, messages, and social features.</span>
                <div className="profile-picture-actions">
                  <button type="button" className="settings-button settings-button--secondary" disabled={profileBusy} onClick={() => profileFileRef.current?.click()}>
                    {profileBusy ? "Working..." : profilePictureUrl ? "Change photo" : "Add photo"}
                  </button>
                  {profilePictureUrl && <button type="button" className="settings-button settings-button--quiet" disabled={profileBusy} onClick={handleRemoveProfilePicture}>Remove</button>}
                </div>
                <input ref={profileFileRef} className="profile-picture-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleProfilePictureChange} />
                {profileError && <div className="settings-error">{profileError}</div>}
              </div>
            </div>
          </section>

          <form onSubmit={handleSettingsSubmit}>
            <section className="settings-section">
              <div className="settings-section-heading">
                <div><h3>Profile & Account</h3><p>How your account appears and how you sign in.</p></div>
              </div>
              <div className="settings-field-grid">
                <label className="settings-field">
                  <span>Display Name</span>
                  <input type="text" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} placeholder={currentUser?.display_name || ""} />
                </label>
                <div className="settings-field">
                  <span>Email</span>
                  <div className="settings-readonly-value">{newEmail || "No email available"}</div>
                  <small>Your sign-in email.</small>
                </div>
              </div>
              <button type="button" className="settings-text-button" onClick={() => setShowPasswordFields((prev) => !prev)}>
                {showPasswordFields ? "Cancel password change" : "Change password"}
              </button>
              {showPasswordFields && (
                <div className="settings-password-grid">
                  <label className="settings-field"><span>New Password</span><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></label>
                  <label className="settings-field"><span>Confirm Password</span><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></label>
                </div>
              )}
            </section>

            <section className="settings-section">
              <div className="settings-section-heading">
                <div><h3>Collection Privacy</h3><p>Choose who can discover and view your collection. Private items always remain visible only to you.</p></div>
              </div>
              <div className="privacy-options" role="radiogroup" aria-label="Collection privacy">
                {[
                  ["public", "Public", "Anyone can view your collection."],
                  ["friends", "Friends Only", "Only you and accepted friends can view it."],
                  ["private", "Private", "Only you can view your collection."],
                ].map(([value, title, description]) => (
                  <label key={value} className={`privacy-option ${collectionVisibility === value ? "is-selected" : ""}`}>
                    <input type="radio" name="collectionVisibility" value={value} checked={collectionVisibility === value} onChange={() => setCollectionVisibility(value)} />
                    <span className="privacy-option-copy"><strong>{title}</strong><span>{description}</span></span>
                    <span className="privacy-radio" aria-hidden="true" />
                  </label>
                ))}
              </div>
              {privacyError && <div className="settings-error">{privacyError}</div>}
            </section>

            <section className="settings-section settings-section--row">
              <div className="settings-section-heading settings-section-heading--compact">
                <div><h3>Appearance</h3><p>Choose the Hobbli theme you prefer.</p></div>
              </div>
              <label className="toggle-switch" aria-label="Toggle light and dark mode">
                <input type="checkbox" checked={isDarkMode} onChange={() => setIsDarkMode((prev) => !prev)} />
                <span className="slider"></span>
              </label>
            </section>

            <div className="settings-save-row">
              <button type="button" className="settings-button settings-button--quiet" onClick={onClose}>Cancel</button>
              <button type="submit" className="settings-button settings-button--primary" disabled={updatingAccount || privacyBusy}>
                {updatingAccount || privacyBusy || saveStatus === "saving"
                  ? "Saving..."
                  : saveStatus === "saved"
                    ? "✓ Saved"
                    : "Save Changes"}
              </button>
            </div>
            {accountError && (
              <div className="settings-save-feedback settings-save-feedback--error" role="alert">
                {accountError}
              </div>
            )}
            {saveStatus === "saved" && (
              <div className="settings-save-feedback settings-save-feedback--success" role="status">
                Your account settings are saved.
              </div>
            )}
          </form>

          <section className="settings-section settings-tools-section">
            <div className="settings-section-heading"><div><h3>Tools & Data</h3><p>Manage your collection data and reports.</p></div></div>
            <div className="settings-tool-grid">
              <button type="button" className="settings-tool-button" onClick={() => setShowReportsModal(true)}><strong>My Dashboard</strong><span>Collection reports and insights</span></button>
              <button type="button" className="settings-tool-button" onClick={() => setShowExportModal(true)}><strong>Export Collection</strong><span>Download your collection data</span></button>
              <button type="button" className="settings-tool-button" onClick={() => setShowImportModal(true)}><strong>Add via Excel</strong><span>Import items from a spreadsheet</span></button>
            </div>
          </section>
        </div>
      </div>

      <ImportCollectionModal show={showImportModal} onClose={() => setShowImportModal(false)} user={currentUser} />
      <ExportCollectionModal show={showExportModal} onClose={() => setShowExportModal(false)} user={currentUser} />
      <UserDashboardModal show={showReportsModal} onClose={() => setShowReportsModal(false)} user={currentUser} isDarkMode={isDarkMode} />
    </>
  );
}
